(() => {
  const config = window.GearRackEffects || {};
  if (!config.enabled) return;

  const root = document.documentElement;
  root.classList.add('gr-motion-js');

  const supportsObserver = 'IntersectionObserver' in window;
  const motionClass = (style) => `gr-motion-${style || 'fade-up'}`;

  // Never animate layout-critical elements. Transforms on these elements can
  // break Flickity, sticky positioning, overlays, and section measurements.
  const unsafeSelector = [
    'slide-show', '.flickity-viewport', '.flickity-slider', '.flickity-enabled',
    '.carousel', '.products', '.row', '.grid', '.columns',
    '[data-flickity]', '[role="dialog"]', 'header', 'footer',
    '.shopify-section-group-header-group', '.shopify-section-group-overlay-group'
  ].join(',');

  const isUnsafe = (element) => {
    if (!element || element.matches(unsafeSelector)) return true;
    if (element.closest('.flickity-viewport, .flickity-slider, [role="dialog"], #shopify-section-header-group, #shopify-section-overlay-group')) return true;
    return false;
  };

  const reveal = (element) => {
    if (!element) return;
    element.classList.add('gr-motion-visible');
    element.style.removeProperty('will-change');
  };

  const observer = supportsObserver
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            requestAnimationFrame(() => reveal(entry.target));
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.01, rootMargin: '120px 0px 120px 0px' })
    : null;

  const prepare = (element, style, delay = 0) => {
    if (!element || element.dataset.grMotionReady === 'true' || isUnsafe(element)) return;

    element.dataset.grMotionReady = 'true';
    element.classList.add('gr-motion', motionClass(style));
    element.style.setProperty('--gr-motion-delay', `${Math.min(Number(delay) || 0, 500)}ms`);

    const rect = element.getBoundingClientRect();
    const nearViewport = rect.top < window.innerHeight + 120 && rect.bottom > -120;

    if (!observer || nearViewport) {
      requestAnimationFrame(() => reveal(element));
    } else {
      observer.observe(element);
    }
  };

  const findSafeBlocks = (section) => {
    const selectors = [
      '.section-header', '.rich-text__inner', '.image-with-text__content',
      '.multicolumn-card', '.collection-card', '.product-card',
      '.gallery__item', '.media-grid__item', '.testimonial',
      '.text-with-icons__block', '.promotion-block'
    ];

    const blocks = section.querySelectorAll(selectors.join(','));
    return [...new Set(blocks)].filter((block) => !isUnsafe(block));
  };

  const setup = (rootNode = document) => {
    const sections = rootNode.matches?.('.shopify-section')
      ? [rootNode]
      : [...(rootNode.querySelectorAll?.('.shopify-section') || [])];

    sections.forEach((section) => {
      if (section.closest('#shopify-section-header-group, #shopify-section-overlay-group')) return;

      // Sections only fade. Translating a whole section changes slider and
      // sticky containing blocks, which caused overlapping content.
      // Do not animate sections that contain fixed side panels (especially the
      // mobile collection filter drawer). Animating/filtering an ancestor can
      // create a containing/stacking context on mobile browsers, causing the
      // page's blurred click-capture overlay to render above the drawer.
      const containsFixedPanel = Boolean(section.querySelector('.side-panel, #Facet-Drawer'));
      if (config.sections && !containsFixedPanel && !section.matches('.section-collection-tabs, .section-slideshow')) {
        prepare(section, 'fade', 0);
      }

      if (config.blocks) {
        findSafeBlocks(section).forEach((block, index) => {
          prepare(block, config.blockStyle, index * (Number(config.stagger) || 0));
        });
      }
    });
  };

  const revealAll = (scope = document) => {
    scope.querySelectorAll?.('.gr-motion').forEach(reveal);
  };

  const start = () => {
    setup(document);

    // Fail open: no page content should remain blank due to an observer,
    // app, lazy-load, or browser timing issue.
    window.setTimeout(() => revealAll(document), 1800);
    window.addEventListener('load', () => window.setTimeout(() => revealAll(document), 250), { once: true });
    window.addEventListener('pageshow', () => revealAll(document));
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();

  document.addEventListener('shopify:section:load', (event) => {
    setup(event.target);
    window.setTimeout(() => revealAll(event.target), 100);
  });

  document.addEventListener('shopify:section:select', (event) => revealAll(event.target));
  document.addEventListener('shopify:block:select', (event) => reveal(event.target));
})();
